function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useEffect, useMemo, useState } from 'react';
import { countBy, isEmpty, keyBy, mapValues, compact } from 'lodash-es';
import { ChipButton, ConfirmationModal, DropdownMenu, FilterPickerGroup, FunctionalLoader, Page, PageContentHeader, PageErrorState, Searchbox, TableList, Typography, useStateInRef } from 'seagull';
import { ArchiveFill, CopyFill, WideArrowDownward, WideArrowUpward } from 'seagull/icons';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import ServiceBuilder from "../../../components/ServiceCreation/ServiceBuilder/ServiceBuilder";
import { useOverlayContext, useStore, useStoreActions } from "../../../contexts";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { useServiceTemplateApi } from "../../../hooks/useServiceTemplateApi";
import { useSetDocumentTitle } from "../../../hooks/useSetDocumentTitle";
import { useSortFromUrl } from "../../../hooks/useSortFromUrl";
import { handlePromise } from "../../../infra/utils";
import { serviceModel, serviceTemplateModel } from "../../../models";
import { ServiceSource } from "../../../models/service/model";
import { selectors } from "../../../store/state-selectors";
import { Accounting } from "../../../types/api.model";
import pluralize from "../../../utils/pluralize";
import styles from "./CommonServicesPage.module.scss";
import { defaultSort, getDuplicatedServices, getServiceNameValidation, saveServiceTemplate, sortFunctions, sortIds } from "./CommonServicesPage.utils";
import { RowItem } from "./RowItem";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function CommonServicesPage(_ref) {
  let {
    headerActions,
    fetchMethod,
    renderEmptyState,
    title,
    subtitle,
    columnDefs,
    headerItems,
    dataSelector,
    bundlesMode: isBundlesMode
  } = _ref;
  const serviceTemplateApi = useServiceTemplateApi();
  const overlayContext = useOverlayContext();
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const showXeroIntegration = useStore(selectors.integration.showXeroIntegration);
  const {
    data,
    loading,
    error
  } = useStore(dataSelector);
  const qboItems = useStore(state => state.qboItems);
  const xeroItems = useStore(state => state.xeroItems);
  const {
    fetchAccountingItems,
    logBI,
    fetchIntegrationsSummary
  } = useStoreActions();
  const sortIndices = useMemo(() => sortIds(headerItems), [headerItems]);
  const {
    sort,
    changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds: sortIndices
  });
  const {
    urlState: {
      search
    },
    changeUrlState
  } = useStateFromUrl({
    search: {
      defaultValue: ''
    }
  });
  const {
    SERVICE_TEMPLATE_BUNDLE
  } = useFeatureFlags();
  const [selected, setSelected] = useState({});
  const getSelected = useStateInRef(selected);
  const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
  const numSelected = useMemo(() => {
    var _countBy$true;
    return (_countBy$true = countBy(selected).true) !== null && _countBy$true !== void 0 ? _countBy$true : 0;
  }, [selected]);
  const allServices = data === null || data === void 0 ? void 0 : data.active;
  useSetDocumentTitle(title);
  useEffect(() => {
    fetchMethod();
  }, [fetchMethod, title]);
  useEffect(() => {
    // this unselects any service that is not found in the newly fetched list of services
    if (!data || isEmpty(getSelected())) return;
    const ids = data.active.map(service => service.id);
    const newSelected = mapValues(getSelected(), (origValue, serviceId) => ids.includes(serviceId) ? origValue : false);
    setSelected(newSelected);
  }, [data]);
  useEffect(() => {
    const promises = compact([showQboIntegration && fetchAccountingItems(Accounting.QBO, !!SERVICE_TEMPLATE_BUNDLE), showXeroIntegration && fetchAccountingItems(Accounting.Xero, !!SERVICE_TEMPLATE_BUNDLE), (showQboIntegration || showXeroIntegration) && fetchIntegrationsSummary()]);
    if (promises.length > 0) {
      Promise.all(promises);
    }
  }, [showQboIntegration, showXeroIntegration, fetchAccountingItems, fetchIntegrationsSummary, SERVICE_TEMPLATE_BUNDLE]);
  const qboItemsById = useMemo(() => {
    var _qboItems$data;
    return keyBy((_qboItems$data = qboItems.data) === null || _qboItems$data === void 0 ? void 0 : _qboItems$data.items, 'id');
  }, [qboItems.data]);
  const xeroItemsById = useMemo(() => {
    var _xeroItems$data;
    return keyBy((_xeroItems$data = xeroItems.data) === null || _xeroItems$data === void 0 ? void 0 : _xeroItems$data.items, 'id');
  }, [xeroItems.data]);
  const services = useMemo(() => {
    if (!data) return null;
    const normalizedSearch = search ? search.trim().toLowerCase() : search;
    const filtered = data.active.filter(service => {
      return !normalizedSearch || service.name.toLowerCase().includes(normalizedSearch);
    });
    if (sort) {
      filtered.sort(sortFunctions[sortIndices[sort.colIndex]](sort.sort === 'asc', qboItemsById));
    }
    return filtered;
  }, [data, qboItemsById, search, sort, sortIndices]);
  const [registerSearchBoxHotkey, setRegisterSearchBoxHotkey] = useState(true);
  async function openBuilder(serviceToEdit) {
    if (serviceToEdit) {
      logBI(11102, {
        serviceId: serviceToEdit.id
      });
    }
    const serviceNameBlacklist = data === null || data === void 0 ? void 0 : data.active.filter(s => s.id !== (serviceToEdit === null || serviceToEdit === void 0 ? void 0 : serviceToEdit.id)).map(s => s.name);
    const serviceTemplateSaverListener = {
      saveStarted: () => overlayContext.startLoader(title),
      saveFailed: () => overlayContext.showErrorToast('Failed to save service', 'save-service-fail-toast'),
      saveFinished: () => overlayContext.stopLoader(title),
      linkFailed: () => overlayContext.showErrorToast('Failed to link service', 'link-service-fail-toast')
    };
    const serviceTemplateRepo = {
      create: serviceTemplateApi.create,
      update: serviceTemplateApi.update
    };
    const storePopulator = {
      populateServiceTemplates: fetchMethod
    };
    const onSave = (output, descriptionObj) => {
      return handlePromise(saveServiceTemplate({
        listener: serviceTemplateSaverListener,
        repo: serviceTemplateRepo,
        modifiedService: serviceTemplateModel.fromService(output),
        originalService: serviceToEdit,
        descriptionObj,
        storePopulator
      }));
    };
    setRegisterSearchBoxHotkey(false);
    try {
      const service = serviceToEdit && serviceModel.fromServiceTemplate(serviceToEdit, ServiceSource.Draft);
      await overlayContext.showModal({
        content: p => /*#__PURE__*/_jsx(ServiceBuilder, _objectSpread(_objectSpread({}, p), {}, {
          serviceOrigin: "template",
          service: service,
          serviceNameBlacklist: serviceNameBlacklist,
          onSave: onSave,
          serviceUsedIn: serviceToEdit === null || serviceToEdit === void 0 ? void 0 : serviceToEdit.usedIn,
          isCreatingBundle: isBundlesMode
        }))
      });
    } finally {
      setRegisterSearchBoxHotkey(true);
    }
  }
  function onToggleAllSelection(selectAll) {
    if (services == null) return;
    setSelected(services.reduce((acc, _ref2) => {
      let {
        id
      } = _ref2;
      return Object.assign(acc, {
        [id]: selectAll
      });
    }, {}));
  }
  async function editService(originalService, edit) {
    try {
      logBI(11101, {
        serviceId: originalService.id
      });
      await serviceTemplateApi.update(_objectSpread(_objectSpread({}, originalService), edit));
      await fetchMethod();
    } catch (err) {
      overlayContext.showErrorToast('The changes were not saved. Please try again', 'service-library-update-error-toast');
    }
  }
  function getSelectedServices() {
    const services = keyBy(data === null || data === void 0 ? void 0 : data.active, 'id');
    return Object.entries(selected).filter(_ref3 => {
      let [, isSelected] = _ref3;
      return isSelected;
    }).flatMap(_ref4 => {
      let [serviceId] = _ref4;
      return serviceId in services ? services[serviceId] : [];
    });
  }
  function bulkActionDecorator(action) {
    return async () => {
      const promises = getSelectedServices().map(service => action(service, false));
      await Promise.all(promises);
      await fetchMethod();
    };
  }
  async function duplicateServices(services) {
    try {
      overlayContext.startLoader(title);
      services.forEach(s => logBI(11104, {
        serviceId: s.id
      }));
      const duplicatedServices = getDuplicatedServices(services, allServices);
      await Promise.all(duplicatedServices.map(serviceTemplateApi.create));
      await fetchMethod();
    } catch (err) {
      overlayContext.showErrorToast('Failed to duplicate service');
    } finally {
      overlayContext.stopLoader(title);
    }
  }
  async function doDelete(service) {
    let refetch = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    try {
      logBI(11103, {
        serviceId: service.id
      });
      overlayContext.startLoader(title);
      await serviceTemplateApi.delete(service.id);
      if (refetch) {
        await fetchMethod();
      }
    } catch (err) {
      overlayContext.showErrorToast('Failed to delete service');
    } finally {
      overlayContext.stopLoader(title);
    }
  }
  async function deleteService(service) {
    const confirmed = await overlayContext.showModal({
      closeOnClickOutside: true,
      shouldBlurBackground: true,
      closeOnEscapeKey: true,
      content: _ref5 => {
        let {
          dismissModal
        } = _ref5;
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          title: "Delete this service?",
          content: "Once this service is deleted you won't be able to access it",
          skin: "danger",
          primaryButtonLabel: "Delete",
          onPrimaryButtonClick: () => dismissModal(true),
          secondaryButtonLabel: "Cancel",
          onSecondaryButtonClick: () => dismissModal(false)
        });
      }
    });
    if (confirmed) {
      return doDelete(service);
    }
  }
  const renderPageContent = () => /*#__PURE__*/_jsxs(TableList.Provider, {
    hasActionColumn: true,
    columnDefs: columnDefs,
    sort: sort,
    onSortChange: changeSort,
    enableRowSelection: true,
    selectedRows: selected,
    onSelectionChange: setSelected,
    numRows: (services === null || services === void 0 ? void 0 : services.length) || 0,
    numSelectedRows: numSelected,
    onToggleAllSelection: onToggleAllSelection,
    children: [(data === null || data === void 0 ? void 0 : data.active.length) > 0 && /*#__PURE__*/_jsxs(PageContentHeader, {
      children: [/*#__PURE__*/_jsxs(FilterPickerGroup, {
        children: [/*#__PURE__*/_jsx(Searchbox, {
          registerHotkey: registerSearchBoxHotkey,
          value: search,
          onChange: event => {
            const search = event.target.value;
            changeUrlState({
              search
            });
          },
          onClear: () => changeUrlState({
            search: ''
          }),
          "data-testid": "service-library-search"
        }), /*#__PURE__*/_jsx(Typography, {
          className: styles.selectedItems,
          "data-testid": "service-library-bulk-counter",
          children: numSelected > 0 ? "".concat(pluralize('service', numSelected, true), " selected") : ''
        }), /*#__PURE__*/_jsxs(DropdownMenu, {
          onOpenChange: setBulkActionsOpen,
          target: /*#__PURE__*/_jsx(ChipButton, {
            "data-testid": "service-library-bulk-actions",
            suffixIcon: bulkActionsOpen ? /*#__PURE__*/_jsx(WideArrowUpward, {}) : /*#__PURE__*/_jsx(WideArrowDownward, {}),
            highlight: bulkActionsOpen,
            disabled: numSelected === 0,
            children: "Bulk actions"
          }),
          children: [/*#__PURE__*/_jsx(DropdownMenu.Item, {
            iconStart: /*#__PURE__*/_jsx(CopyFill, {}),
            "data-testid": "service-library-bulk-duplicate",
            onClick: () => duplicateServices(getSelectedServices()),
            children: "Duplicate"
          }), /*#__PURE__*/_jsx(DropdownMenu.Item, {
            skin: "error",
            iconStart: /*#__PURE__*/_jsx(ArchiveFill, {}),
            "data-testid": "service-library-bulk-delete",
            onClick: bulkActionDecorator(doDelete),
            children: "Delete"
          })]
        })]
      }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(TableList.Header, {
        items: headerItems,
        "data-testid": "service-library-table-header"
      })]
    }), services === null || services === void 0 ? void 0 : services.map(service => /*#__PURE__*/_jsx(RowItem, {
      service: service,
      qboItemsById: qboItemsById,
      xeroItemsById: xeroItemsById,
      serviceNameValidation: getServiceNameValidation({
        skipServiceId: service.id,
        allServices
      }),
      onInlineEdit: edit => editService(service, edit),
      onEdit: () => openBuilder(service),
      onDuplicate: () => duplicateServices([service]),
      onDelete: () => deleteService(service),
      onInputFocus: () => setRegisterSearchBoxHotkey(false),
      onInputBlur: () => setRegisterSearchBoxHotkey(true),
      columns: headerItems.map(_ref6 => {
        let {
          id
        } = _ref6;
        return id;
      }),
      isHeightFluid: isBundlesMode
    }, service.id)), (data === null || data === void 0 ? void 0 : data.active.length) === 0 && renderEmptyState({
      openBuilder
    })]
  });
  if (error) return /*#__PURE__*/_jsx(PageErrorState, {
    title: title,
    onRefresh: () => fetchMethod()
  });
  return !data ? /*#__PURE__*/_jsx(FunctionalLoader, {
    show: true,
    name: title
  }) : /*#__PURE__*/_jsx(Page, {
    skin: "secondary",
    "data-testid": "service-library",
    header: title + (data && data.active.length > 0 ? " (".concat(data.active.length, ")") : ''),
    subheader: subtitle,
    loading: loading,
    actions: headerActions({
      openBuilder
    }),
    children: renderPageContent()
  });
}